/* MUSOU behaviour for Prestige (Cows Can Fly, October 2026). One library: Lenis 1.3.26 (MIT) for smooth scroll.
   Each part starts only where its section is on the page, and again when a section is (re)loaded in the theme editor.
   Visitors who prefer reduced motion get every section open and still. */
(function () {
  'use strict';
  var root = document.documentElement;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var designMode = !!(window.Shopify && window.Shopify.designMode);
  var clamp = function (v, a, b) { return Math.min(b, Math.max(a, v)); };
  var lerp = function (a, b, t) { return a + (b - a) * t; };
  var qa = function (s, el) { return Array.prototype.slice.call((el || document).querySelectorAll(s)); };

  /* one scroll/resize listener drives every scroll-linked part */
  var scrollers = [];
  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      ticking = false;
      scrollers = scrollers.filter(function (f) { return f() !== false; });
    });
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);

  /* ---------- reveals ---------- */
  var io = 'IntersectionObserver' in window ? new IntersectionObserver(function (es) {
    es.forEach(function (e) { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
  }, { rootMargin: '0px 0px -8% 0px' }) : null;
  function reveals(scope) {
    qa('[data-musou-reveal]', scope).forEach(function (el, i) {
      if (!io || reduce) { el.classList.add('is-in'); return; }
      el.style.transitionDelay = (i % 4) * 70 + 'ms';
      io.observe(el);
    });
  }

  /* ---------- 1 · the film and the stitched intro ---------- */
  function film(scope) {
    qa('[data-musou-film]', scope).forEach(function (sec) {
      var video = sec.querySelector('.mf__video');
      var btn = sec.querySelector('[data-film-toggle]');
      var paused = reduce;
      if (video) {
        var portrait = window.innerHeight > window.innerWidth * 1.05;
        var src = portrait ? video.getAttribute('data-src-mobile') : video.getAttribute('data-src');
        if (portrait && video.getAttribute('data-poster-mobile')) video.poster = video.getAttribute('data-poster-mobile');
        var load = function () { if (!video.getAttribute('src')) { video.src = src; video.preload = 'auto'; } };
        if (!reduce) load();
        var label = function () {
          if (!btn) return;
          btn.setAttribute('aria-pressed', String(paused));
          btn.querySelector('.mf__pause-t').textContent = paused ? (btn.getAttribute('data-label-play') || 'Play film') : (btn.getAttribute('data-label-pause') || 'Pause film');
        };
        label();
        if (btn) btn.addEventListener('click', function () {
          paused = !paused;
          if (paused) video.pause(); else { load(); video.play().catch(function () {}); }
          label();
        });
        if ('IntersectionObserver' in window) {
          new IntersectionObserver(function (es) {
            es.forEach(function (e) { if (e.isIntersecting && !paused) video.play().catch(function () {}); else video.pause(); });
          }, { threshold: .1 }).observe(video);
        }
      }
      intro(sec);
    });
  }

  function intro(sec) {
    var pre = sec.querySelector('.mf__pre');
    if (!pre) return;
    var seen = root.classList.contains('musou-intro-seen');
    if (seen || reduce || designMode) { pre.remove(); return; }
    try { sessionStorage.setItem('musou-intro', '1'); } catch (e) {}
    document.body.appendChild(pre); // fixed to the viewport, whatever transforms the theme puts on its sections
    var cameo = pre.querySelector('.mf__cameo');
    window.scrollTo(0, 0);
    root.style.overflow = 'hidden';
    var speed = 1, opened = false, done = false;
    var timers = [];
    function at(ms, fn) { timers.push(setTimeout(fn, ms / speed)); }
    function hurry() {
      if (speed > 1 || done) return;
      speed = 3;
      timers.forEach(clearTimeout); timers = [];
      pre.classList.add('is-stitched', 'is-filled');
      if (!opened) open(true);
    }
    ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach(function (t) { window.addEventListener(t, hurry, { passive: true, once: true }); });

    var r = { x: 0, y: 0 };
    function paint() { pre.style.setProperty('--rx', r.x + 'px'); pre.style.setProperty('--ry', r.y + 'px'); }
    function tween(tx, ty, dur, ease, done) {
      var fx = r.x, fy = r.y, t0 = performance.now();
      (function step(now) {
        var t = clamp((now - t0) / dur, 0, 1), k = ease(t);
        r.x = lerp(fx, tx, k); r.y = lerp(fy, ty, k); paint();
        if (t < 1) requestAnimationFrame(step); else if (done) done();
      })(t0);
    }
    var expoOut = function (t) { return t === 1 ? 1 : 1 - Math.pow(2, -10 * t); };
    var expoInOut = function (t) { return t === 0 || t === 1 ? t : t < .5 ? Math.pow(2, 20 * t - 10) / 2 : (2 - Math.pow(2, -20 * t + 10)) / 2; };
    function open(fast) {
      opened = true;
      var box = cameo.getBoundingClientRect();
      var far = Math.hypot(window.innerWidth, window.innerHeight);
      pre.classList.add('is-open');
      tween(box.width * .41, box.height * .44, fast ? 200 : 600, expoOut, function () {
        setTimeout(function () {
          pre.classList.add('is-leaving');
          tween(far, far, fast ? 600 : 1200, expoInOut, function () { done = true; pre.remove(); root.style.overflow = ''; onScroll(); });
        }, fast ? 0 : 250);
      });
    }
    requestAnimationFrame(function () { pre.classList.add('is-stitched'); });
    at(1150, function () { pre.classList.add('is-filled'); });
    at(1750, function () { open(false); });
  }

  /* ---------- 2 · the window opens as she scrolls ---------- */
  function windowScene(scope) {
    qa('[data-musou-window]', scope).forEach(function (sec) {
      if (reduce) { sec.style.setProperty('--p', 1); return; }
      var wide = window.matchMedia('(min-width: 1024px)');
      var frame = sec.querySelector('.mw__frame') || sec;
      scrollers.push(function () {
        if (!sec.isConnected) return false;
        var r = sec.getBoundingClientRect(), vh = window.innerHeight, p;
        if (wide.matches) p = clamp(-r.top / Math.max(1, r.height - vh), 0, 1);
        // stacked (phones, tablets): follow the photo itself, from its top entering at 92% of the screen to its top
        // reaching 22%, so the window finishes opening as the photo settles into view, not while it is still below
        else p = clamp((vh * .92 - frame.getBoundingClientRect().top) / (vh * .7), 0, 1);
        sec.style.setProperty('--p', p.toFixed(4));
      });
    });
    onScroll();
  }

  /* ---------- 3 · the collection: a slow drift that can be dragged, with tabs that filter in place ---------- */
  function rail(scope) {
    qa('[data-musou-rail]', scope).forEach(function (sec) {
      var wrap = sec.querySelector('.mr__rail'), track = sec.querySelector('.mr__track');
      if (!wrap || !track) return;
      var cards = qa('.mr__card', track), tabs = qa('.mr__tab', sec);
      var x = 0, v = 0, setW = 0, loop = false, hover = false, focus = false, inView = false, drag = null, suppress = false, clones = [];
      var AUTO = reduce ? 0 : .38;
      var gap = function () { return parseFloat(getComputedStyle(track).columnGap) || 0; };
      var visible = function () { return cards.filter(function (c) { return !c.classList.contains('is-out'); }); };
      function build() {
        clones.forEach(function (c) { c.remove(); });
        clones = []; x = 0; v = 0;
        track.style.transform = 'translate3d(0,0,0)';
        var vis = visible();
        if (!vis.length) return;
        setW = vis[vis.length - 1].getBoundingClientRect().right - vis[0].getBoundingClientRect().left + gap();
        loop = setW > wrap.clientWidth * 1.05 && !reduce;
        wrap.classList.toggle('is-static', !loop);
        if (!loop) return;
        var total = setW;
        while (total < setW + wrap.clientWidth + 200) {
          vis.forEach(function (c) {
            var k = c.cloneNode(true);
            k.setAttribute('aria-hidden', 'true');
            qa('a, button', k).forEach(function (a) { a.tabIndex = -1; });
            track.appendChild(k); clones.push(k);
          });
          total += setW;
        }
      }
      tabs.forEach(function (t) {
        t.addEventListener('click', function () {
          var types = t.getAttribute('data-types');
          var list = types === '*' ? null : types.split('|');
          tabs.forEach(function (o) { var on = o === t; o.classList.toggle('is-on', on); o.setAttribute('aria-pressed', String(on)); });
          track.style.transition = 'opacity .3s'; track.style.opacity = '0';
          setTimeout(function () {
            cards.forEach(function (c) { c.classList.toggle('is-out', !!list && list.indexOf(c.getAttribute('data-type')) < 0); });
            build();
            track.style.opacity = '1';
          }, 300);
        });
      });
      qa('.mr__arrow', sec).forEach(function (b) {
        b.addEventListener('click', function () {
          var c = visible()[0];
          v = -parseFloat(b.getAttribute('data-dir')) * ((c ? c.getBoundingClientRect().width : 300) + gap()) * .08;
        });
      });
      wrap.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') hover = true; });
      wrap.addEventListener('pointerleave', function () { hover = false; });
      wrap.addEventListener('focusin', function () { focus = true; });
      wrap.addEventListener('focusout', function () { focus = false; });
      wrap.addEventListener('pointerdown', function (e) { if (loop && e.button === 0) { drag = { x0: e.clientX, y0: e.clientY, xs: x, last: e.clientX, moved: false }; v = 0; } });
      window.addEventListener('pointermove', function (e) {
        if (!drag) return;
        var dx = e.clientX - drag.x0, dy = e.clientY - drag.y0;
        if (!drag.moved) {
          if (Math.abs(dx) > 6 && Math.abs(dx) > Math.abs(dy)) { drag.moved = true; wrap.classList.add('is-dragging'); }
          else if (Math.abs(dy) > 10) { drag = null; return; }
        }
        if (drag.moved) { x = drag.xs + dx; v = (e.clientX - drag.last) * .9; drag.last = e.clientX; }
      }, { passive: true });
      var end = function () { if (drag && drag.moved) { suppress = true; setTimeout(function () { suppress = false; }, 0); } drag = null; wrap.classList.remove('is-dragging'); };
      window.addEventListener('pointerup', end);
      window.addEventListener('pointercancel', end);
      wrap.addEventListener('click', function (e) { if (suppress) { e.preventDefault(); e.stopPropagation(); } }, true);
      wrap.addEventListener('wheel', function (e) { if (loop && Math.abs(e.deltaX) > Math.abs(e.deltaY)) { e.preventDefault(); x -= e.deltaX; } }, { passive: false });
      if ('IntersectionObserver' in window) new IntersectionObserver(function (es) { inView = es[0].isIntersecting; }).observe(wrap); else inView = true;
      (function tick() {
        if (!sec.isConnected) return;
        if (loop && inView) {
          if (!drag) { x += v; v *= .93; if (Math.abs(v) < .05) v = 0; if (!hover && !focus && Math.abs(v) < .6) x -= AUTO; }
          x = ((x % setW) + setW) % setW - setW;
          track.style.transform = 'translate3d(' + x.toFixed(2) + 'px,0,0)';
        }
        requestAnimationFrame(tick);
      })();
      var t;
      window.addEventListener('resize', function () { clearTimeout(t); t = setTimeout(build, 250); });
      window.addEventListener('load', build);
      build();
    });
  }

  /* ---------- 4 · occasions: a slow parallax ---------- */
  function occasions(scope) {
    if (reduce) return;
    qa('.mo__panel', scope).forEach(function (p) {
      scrollers.push(function () {
        if (!p.isConnected) return false;
        var r = p.getBoundingClientRect(), vh = window.innerHeight;
        if (r.bottom < 0 || r.top > vh) return;
        p.style.setProperty('--py', (((vh - r.top) / (vh + r.height)) * 10 - 5).toFixed(2));
      });
    });
    onScroll();
  }

  /* ---------- 5 · made to order: the running stitch and the order-by date ---------- */
  function madeToOrder(scope) {
    qa('[data-musou-mto]', scope).forEach(function (sec) {
      var steps = sec.querySelector('.mm__steps');
      if (steps && !reduce) {
        scrollers.push(function () {
          if (!sec.isConnected) return false;
          var r = steps.getBoundingClientRect(), vh = window.innerHeight;
          sec.style.setProperty('--p', clamp((vh * .72 - r.top) / Math.max(1, r.height + vh * .2), 0, 1).toFixed(4));
        });
        onScroll();
      }
      var form = sec.querySelector('.mm__when');
      if (!form) return;
      var input = form.querySelector('input[type=date]'), out = form.querySelector('.mm__a');
      var days = parseInt(form.getAttribute('data-lead-days'), 10) || 66;
      var mail = form.getAttribute('data-contact') || '';
      var today = new Date(); today.setHours(0, 0, 0, 0);
      input.min = new Date(today.getTime() - today.getTimezoneOffset() * 6e4).toISOString().slice(0, 10);
      var f = new Intl.DateTimeFormat('en-AU', { weekday: 'long', day: 'numeric', month: 'long' });
      var msg = function (k, d) { return form.getAttribute('data-msg-' + k) || d; };
      /* the messages come from the section settings: [date] becomes the order-by date, [words] become the email link */
      var say = function (text, date, ev) {
        out.textContent = '';
        text.split(/(\[[^\]]+\])/).forEach(function (part) {
          var m = part.match(/^\[([^\]]+)\]$/);
          if (!m) { out.appendChild(document.createTextNode(part)); return; }
          if (m[1] === 'date') { var b = document.createElement('strong'); b.textContent = f.format(date); out.appendChild(b); return; }
          if (!mail) { out.appendChild(document.createTextNode(m[1])); return; }
          var a = document.createElement('a');
          a.href = 'mailto:' + mail + '?subject=' + encodeURIComponent('An event on ' + f.format(ev));
          a.textContent = m[1];
          out.appendChild(a);
        });
      };
      input.addEventListener('input', function () {
        if (!input.value) { say(msg('empty', 'Choose a date to see your order-by day.')); return; }
        var ev = new Date(input.value + 'T00:00:00');
        var ob = new Date(ev); ob.setDate(ob.getDate() - days);
        if (ev < today) { say(msg('past', 'That date has passed. Choose your next occasion.')); return; }
        if (ob < today) say(msg('tight', 'That is inside our usual window. [Write to us] and we will tell you honestly what is possible.'), ob, ev);
        else say(msg('ok', 'Order by [date] and your piece will be with you in good time.'), ob, ev);
      });
    });
  }

  /* ---------- 6 · the hoop magnifies the handwork ---------- */
  function hoop(scope) {
    qa('[data-musou-hoop]', scope).forEach(function (stage) {
      var img = stage.querySelector('.mc__img'), ring = stage.querySelector('.mc__hoop'), lens = stage.querySelector('.mc__lens');
      if (!img || !ring || img.tagName !== 'IMG') return;
      var Z = parseFloat(stage.getAttribute('data-zoom')) || 2.1;
      var rest = { x: parseFloat(stage.getAttribute('data-rest-x')) || .62, y: parseFloat(stage.getAttribute('data-rest-y')) || .74 };
      var pos = { x: rest.x, y: rest.y }, target = { x: rest.x, y: rest.y }, mouse = false, inView = false, t0 = performance.now();
      function apply() {
        var w = img.clientWidth, h = img.clientHeight;
        if (!w) return;
        var px = pos.x * w, py = pos.y * h, d = ring.offsetWidth - 20;
        ring.style.setProperty('--hx', px + 'px'); ring.style.setProperty('--hy', py + 'px');
        lens.style.backgroundSize = (w * Z) + 'px ' + (h * Z) + 'px';
        lens.style.backgroundPosition = (-(px * Z - d / 2)) + 'px ' + (-(py * Z - d / 2)) + 'px';
      }
      function setSrc() { lens.style.backgroundImage = 'url("' + (img.currentSrc || img.src) + '")'; apply(); }
      if (img.complete && img.naturalWidth) setSrc(); else img.addEventListener('load', setSrc);
      stage.addEventListener('pointermove', function (e) {
        if (e.pointerType !== 'mouse') return;
        mouse = true;
        var r = img.getBoundingClientRect();
        target.x = clamp((e.clientX - r.left) / r.width, .08, .92);
        target.y = clamp((e.clientY - r.top) / r.height, .06, .94);
      });
      stage.addEventListener('pointerleave', function () { mouse = false; });
      /* phones and tablets: a tap places the hoop, then it drifts again */
      var resume = 0;
      stage.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'mouse') return;
        var r = img.getBoundingClientRect();
        target.x = clamp((e.clientX - r.left) / r.width, .08, .92);
        target.y = clamp((e.clientY - r.top) / r.height, .06, .94);
        mouse = true;
        clearTimeout(resume);
        resume = setTimeout(function () { mouse = false; }, 3500);
      });
      if ('IntersectionObserver' in window) new IntersectionObserver(function (es) { inView = es[0].isIntersecting; }).observe(stage); else inView = true;
      window.addEventListener('resize', apply);
      (function tick(now) {
        if (!stage.isConnected) return;
        if (inView) {
          if (!mouse && !reduce) {
            var t = ((now || performance.now()) - t0) / 1000;
            target.x = clamp(rest.x + Math.sin(t * .42) * .17, .08, .92);
            target.y = clamp(rest.y + Math.sin(t * .31 + 1.2) * .1, .06, .94);
          }
          pos.x = lerp(pos.x, target.x, mouse ? .2 : .05);
          pos.y = lerp(pos.y, target.y, mouse ? .2 : .05);
          apply();
        }
        requestAnimationFrame(tick);
      })();
    });
  }

  /* ---------- 7 · pearls on the look ---------- */
  function pearls(scope) {
    var all = qa('.ml__pearl', scope);
    if (!all.length) return;
    function closeAll(except) { qa('.ml__pearl.is-open').forEach(function (p) { if (p !== except) { p.classList.remove('is-open'); p.querySelector('.ml__btn').setAttribute('aria-expanded', 'false'); } }); }
    all.forEach(function (p) {
      var b = p.querySelector('.ml__btn');
      b.addEventListener('click', function (e) {
        e.stopPropagation();
        var open = !p.classList.contains('is-open');
        closeAll(p); p.classList.toggle('is-open', open); b.setAttribute('aria-expanded', String(open));
        /* open the card towards the side with room, so it never runs off a phone screen */
        if (open) { p.classList.remove('is-left'); var c = p.querySelector('.ml__card').getBoundingClientRect(); if (c.right > document.documentElement.clientWidth - 8) p.classList.add('is-left'); }
      });
    });
    document.addEventListener('click', function (e) { if (!e.target.closest('.ml__pearl')) closeAll(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeAll(); });
  }

  /* ---------- 8 · the strip loops seamlessly with a second, hidden copy ---------- */
  function strip(scope) {
    qa('[data-musou-strip]', scope).forEach(function (track) {
      if (track.hasAttribute('data-cloned') || reduce) return;
      track.setAttribute('data-cloned', '');
      qa('.ms__item', track).forEach(function (li) {
        var k = li.cloneNode(true);
        k.setAttribute('aria-hidden', 'true');
        qa('img', k).forEach(function (i) { i.alt = ''; });
        track.appendChild(k);
      });
    });
  }

  /* ---------- 10 · the invitation card tilts a little under the cursor ---------- */
  function tilt(scope) {
    if (!fine || reduce) return;
    qa('[data-musou-tilt]', scope).forEach(function (card) {
      var rx = 0, ry = 0, tx = 0, ty = 0, running = false;
      function loop() {
        rx = lerp(rx, tx, .12); ry = lerp(ry, ty, .12);
        card.style.transform = 'perspective(1100px) rotateX(' + rx.toFixed(2) + 'deg) rotateY(' + ry.toFixed(2) + 'deg)';
        if (Math.abs(rx - tx) > .01 || Math.abs(ry - ty) > .01) requestAnimationFrame(loop); else running = false;
      }
      function kick() { if (!running) { running = true; requestAnimationFrame(loop); } }
      card.addEventListener('pointermove', function (e) {
        var r = card.getBoundingClientRect(), px = (e.clientX - r.left) / r.width, py = (e.clientY - r.top) / r.height;
        tx = (.5 - py) * 7; ty = (px - .5) * 9;
        card.style.setProperty('--sx', px * 100 + '%'); card.style.setProperty('--sy', py * 100 + '%');
        kick();
      });
      card.addEventListener('pointerleave', function () { tx = 0; ty = 0; kick(); });
    });
  }

  /* ---------- size chart: centimetres or inches ---------- */
  function sizes(scope) {
    qa('[data-musou-sizes]', scope).forEach(function (box) {
      qa('.msz__unit', box).forEach(function (b) {
        b.addEventListener('click', function () {
          var u = b.getAttribute('data-unit');
          qa('.msz__unit', box).forEach(function (o) { var on = o === b; o.classList.toggle('is-on', on); o.setAttribute('aria-pressed', String(on)); });
          qa('[data-table]', box).forEach(function (t) { t.hidden = t.getAttribute('data-table') !== u; });
        });
      });
    });
  }

  /* ---------- 12 · WhatsApp button: lift it above the product page's sticky add-to-cart bar on phones ---------- */
  function whatsapp() {
    var btn = document.querySelector('[data-musou-whatsapp]');
    if (!btn || btn.hasAttribute('data-watching')) return;
    /* keep the opening film clear: the button appears once the film is mostly scrolled past */
    var filmSec = document.querySelector('[data-musou-film]');
    if (filmSec && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (es) { btn.classList.toggle('mwa--away', es[0].isIntersecting); }, { threshold: 0.35 }).observe(filmSec);
    }
    var bar = document.querySelector('.product-sticky-bar');
    if (!bar) { btn.setAttribute('data-watching', ''); return; }
    btn.setAttribute('data-watching', '');
    var sync = function () {
      var r = bar.getBoundingClientRect();
      var atBottom = bar.classList.contains('is-visible') && r.bottom >= window.innerHeight - 2;
      btn.style.setProperty('--mwa-lift', atBottom ? Math.round(r.height) + 'px' : '0px');
    };
    new MutationObserver(sync).observe(bar, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('resize', sync);
    sync();
  }

  function init(scope) {
    [film, windowScene, rail, occasions, madeToOrder, hoop, pearls, strip, tilt, sizes, reveals, whatsapp].forEach(function (fn) {
      try { fn(scope); } catch (e) { if (window.console) console.error('MUSOU', fn.name, e); }
    });
  }

  /* ---------- smooth scroll (Lenis), as in the approved prototype: mouse and trackpad only, never in the theme editor.
     autoToggle pauses it whenever Prestige locks the page for a drawer, menu or pop-up; anything inside those
     scrolls natively. ---------- */
  function smoothScroll() {
    if (reduce || !fine || designMode || !window.Lenis || window.__lenis) return;
    var lenis = new window.Lenis({
      lerp: 0.085, wheelMultiplier: 0.95, smoothWheel: true, autoToggle: true, allowNestedScroll: true, anchors: true,
      prevent: function (node) {
        return !!(node.closest && node.closest('x-drawer, x-modal, x-popover, dialog, dialog-element, predictive-search, .header__dropdown-menu, .mega-menu, [data-lenis-prevent]'));
      }
    });
    window.__lenis = lenis;
    (function raf(t) { lenis.raf(t); requestAnimationFrame(raf); })(performance.now());
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { smoothScroll(); init(document); });
  else { smoothScroll(); init(document); }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
